import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Menu, X, Mail, Instagram } from "lucide-react";
import { useState } from "react";

function useNavItems() {
  const { t } = useLang();
  return [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/the-180-project", label: t("nav.project180") },
    { to: "/library", label: t("nav.library") },
    { to: "/youth-leaders", label: t("nav.youth") },
    { to: "/courses", label: t("nav.courses") },
    { to: "/get-involved", label: t("nav.getInvolved") },
  ];
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const navItems = useNavItems();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="border-b border-[#DCDCDC] bg-white sticky top-0 z-50" data-testid="site-header">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-8">
          <Link to="/" className="flex flex-col leading-tight shrink-0" data-testid="brand-link">
            <span className="font-headline text-xl md:text-2xl font-bold text-[#111111]">The New School</span>
            <span className="font-headline text-xl md:text-2xl font-bold text-[#111111]">of Thoughts</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10 xl:gap-12 flex-1 justify-center">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `font-ui text-[11px] uppercase tracking-[0.2em] font-semibold whitespace-nowrap transition-colors ${
                    isActive ? "text-[#111111]" : "text-[#6C6C6C] hover:text-[#111111]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-6 shrink-0">
            <LanguageSwitcher />
            <span className="h-8 w-px bg-[#DCDCDC]" aria-hidden="true" />
            {user ? (
              <>
                <Link to="/dashboard" className="font-ui text-[11px] uppercase tracking-[0.2em] font-semibold text-[#111111]" data-testid="nav-dashboard">
                  {t("cta.myCard")}
                </Link>
                <button onClick={logout} className="btn-outline" data-testid="logout-btn">{t("cta.logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="font-ui text-[11px] uppercase tracking-[0.2em] font-semibold text-[#111111] hover:text-[#6C6C6C] transition-colors" data-testid="nav-login">{t("cta.login")}</Link>
                <Link to="/signup" className="btn-primary" data-testid="nav-signup">{t("cta.getCard")}</Link>
              </>
            )}
          </div>

          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#DCDCDC] bg-white">
            <div className="px-6 py-4 flex flex-col gap-4">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="font-ui text-xs uppercase tracking-[0.2em] font-semibold text-[#111111]"
                >
                  {n.label}
                </NavLink>
              ))}
              <hr className="border-t border-[#DCDCDC] my-2" />
              <div className="pb-2"><LanguageSwitcher inMobileMenu={true} /></div>
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="font-ui text-xs uppercase tracking-[0.2em] font-semibold">{t("cta.myCard")}</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="btn-outline text-left w-fit">{t("cta.logout")}</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="font-ui text-xs uppercase tracking-[0.2em] font-semibold">{t("cta.login")}</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="btn-primary w-fit">{t("cta.getCard")}</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[#DCDCDC] bg-white mt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="font-headline text-2xl font-bold">The New School of Thoughts</div>
            <p className="font-body text-[#3A3A3A] mt-4 max-w-md">
              A global nonprofit advancing self-governance, social justice, participatory governance, and decolonial thought. Grounded in service, evidence, and care.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href="mailto:thenewschoolofthoughts@gmail.com"
                className="inline-flex items-center gap-3 font-ui text-[11px] tracking-[0.2em] uppercase font-semibold text-[#111111] hover:text-[#6C6C6C] transition-colors"
                data-testid="footer-email"
              >
                <Mail size={14} strokeWidth={1.5} />
                thenewschoolofthoughts@gmail.com
              </a>
              <a
                href="https://www.instagram.com/thenewschool_of_thoughts?utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 font-ui text-[11px] tracking-[0.2em] uppercase font-semibold text-[#111111] hover:text-[#6C6C6C] transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={14} strokeWidth={1.5} />
                @thenewschool_of_thoughts
              </a>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-4">{t("footer.explore")}</div>
            <ul className="space-y-2 font-body text-[#111111]">
              <li><Link to="/the-180-project">{t("nav.project180")}</Link></li>
              <li><Link to="/library">{t("nav.library")}</Link></li>
              <li><Link to="/youth-leaders">{t("nav.youth")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-4">{t("footer.engage")}</div>
            <ul className="space-y-2 font-body text-[#111111]">
              <li><Link to="/get-involved">{t("nav.getInvolved")}</Link></li>
              <li><Link to="/signup">{t("cta.getCard")}</Link></li>
              <li><Link to="/about">{t("nav.about")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 border-t border-[#DCDCDC] flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C]">
            © {new Date().getFullYear()} The New School of Thoughts
          </p>
          <p className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C]">{t("footer.tagline")}</p>
        </div>
      </footer>
    </div>
  );
}
